// Single-instance modal layer. Owns open/close + ESC + backdrop dismiss.
// Per-modal helpers (info / cta / video) just call .open() with rendered content.

import { getAnalytics } from '../playcanvas/analytics';

export interface ModalAction {
  label: string;
  url?: string;
  onClick?: () => void;
  primary?: boolean;
}

export interface ModalContent {
  /** Internal id used for analytics tagging — pass the hotspot id where applicable. */
  id: string;
  title: string;
  bodyHtml?: string;
  bodyNode?: HTMLElement;
  actions?: ModalAction[];
}

export class ModalRoot {
  private readonly root: HTMLElement;
  private currentId: string | null = null;
  private onCloseListeners: Array<() => void> = [];

  constructor() {
    const el = document.getElementById('modal-root');
    if (!el) throw new Error('[modal] #modal-root missing');
    this.root = el;
    document.addEventListener('keydown', this.onKey);
    this.root.addEventListener('click', (e) => {
      if (e.target === this.root) this.close();
    });
  }

  open(content: ModalContent): void {
    if (this.currentId === content.id) return;
    this.currentId = content.id;
    this.root.innerHTML = '';
    this.root.classList.add('is-active');

    const modal = document.createElement('div');
    modal.className = 'modal';
    modal.role = 'dialog';
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('aria-label', content.title);

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'modal__close';
    closeBtn.setAttribute('aria-label', 'Fechar');
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', () => this.close());

    const heading = document.createElement('h2');
    heading.textContent = content.title;

    modal.appendChild(closeBtn);
    modal.appendChild(heading);
    if (content.bodyHtml) {
      const body = document.createElement('div');
      body.innerHTML = content.bodyHtml;
      modal.appendChild(body);
    }
    if (content.bodyNode) {
      modal.appendChild(content.bodyNode);
    }

    if (content.actions && content.actions.length > 0) {
      const actions = document.createElement('div');
      actions.className = 'modal__actions';
      for (const action of content.actions) {
        const btn = document.createElement(action.url ? 'a' : 'button');
        btn.className = 'pill-button';
        if (action.primary) btn.classList.add('is-active');
        btn.textContent = action.label;
        if (action.url && btn instanceof HTMLAnchorElement) {
          btn.href = action.url;
          btn.target = '_blank';
          btn.rel = 'noopener noreferrer';
        }
        if (btn instanceof HTMLButtonElement) {
          btn.type = 'button';
        }
        // Every action button closes the modal on click — and runs onClick first if provided.
        // Previously a bare-label button (e.g. "Fechar") had no listener at all and was inert.
        btn.addEventListener('click', () => {
          try {
            action.onClick?.();
          } catch (err) {
            console.error('[modal] action onClick threw', err);
          }
          this.close();
        });
        actions.appendChild(btn);
      }
      modal.appendChild(actions);
    }

    this.root.appendChild(modal);
    getAnalytics().log('modal_opened', { id: content.id });
  }

  close(): void {
    if (!this.currentId) return;
    const id = this.currentId;
    this.currentId = null;
    this.root.innerHTML = '';
    this.root.classList.remove('is-active');
    getAnalytics().log('modal_closed', { id });
    for (const fn of this.onCloseListeners) {
      try {
        fn();
      } catch (err) {
        console.error('[modal] close listener threw', err);
      }
    }
  }

  isOpen(): boolean {
    return this.currentId !== null;
  }

  onClose(fn: () => void): void {
    this.onCloseListeners.push(fn);
  }

  private readonly onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape' && this.isOpen()) this.close();
  };
}
